import { useState } from 'react';

interface NewsletterSectionProps {
  submitAddr: string;
}

export default function NewsletterSection({ submitAddr }: NewsletterSectionProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt' && typeof value === 'string') {
          params.append(key, value);
        }
      });

      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const msg =
          (parsed?.meta as Record<string, string>)?.message ||
          (parsed?.meta as Record<string, string>)?.detail ||
          (typeof parsed?.message === 'string' ? parsed.message : '') ||
          '订阅失败，请稍后重试';
        setErrorMsg(msg);
        setStatus('error');
      }
    } catch {
      setErrorMsg('网络错误，请检查连接后重试');
      setStatus('error');
    }
  };

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-secondary-50">
      <div className="max-w-2xl mx-auto text-center">
        <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <i className="ri-mail-send-line text-2xl"></i>
        </div>
        <h2 className="mt-4 font-heading text-xl md:text-2xl font-semibold text-foreground-900">
          订阅我们的 Newsletter
        </h2>
        <p className="mt-2 text-sm text-foreground-600">
          每周获取最新的航空旅行评测、独家航线推荐和飞行攻略
        </p>

        {status === 'success' ? (
          <div className="mt-6 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700">
            <i className="ri-check-line mr-1"></i>
            订阅成功！感谢你的关注，我们将每周为你带来精彩的航空旅行内容。
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="mt-6 flex flex-col sm:flex-row items-stretch gap-2"
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="输入你的邮箱地址"
              required
              className="flex-1 px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-full focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
            />
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="form-honeypot"
            />
            <button
              type="submit"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              订阅
            </button>
          </form>
        )}

        {status === 'error' && (
          <p className="mt-3 text-xs text-red-600">{errorMsg}</p>
        )}
      </div>
    </section>
  );
}